import { Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { IMAGES, SITE } from "@/data/content";

const socials = [
  { key: "youtube", url: SITE.youtube, Icon: Youtube, testId: "about-social-youtube" },
  { key: "instagram", url: SITE.instagram, Icon: Instagram, testId: "about-social-instagram" },
  { key: "facebook", url: SITE.facebook, Icon: Facebook, testId: "about-social-facebook" },
  { key: "twitter", url: SITE.twitter, Icon: Twitter, testId: "about-social-twitter" },
].filter((s) => s.url);

export const About = () => {
  const { t } = useLang();
  return (
    <section id="about" data-testid="about-section" className="bg-ink py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 md:grid-cols-2 md:gap-16 md:px-8 lg:gap-24">
        <Reveal className="relative">
          <div className="overflow-hidden rounded-md border border-line">
            <img
              src={IMAGES.about}
              alt="Journalist Jaffar at the microphone"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover object-[center_35%]"
            />
          </div>
          <div className="absolute -bottom-5 -right-3 h-24 w-24 border-b-2 border-r-2 border-ember md:-right-5" aria-hidden="true" />
        </Reveal>

        <div>
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-ember">
              <span className="h-px w-8 bg-ember" aria-hidden="true" />
              {t.about.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-cream sm:text-5xl">
              {t.about.title}{" "}
              <span className="font-editorial font-medium italic text-ember">{t.about.titleItalic}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base leading-relaxed text-cream/90 md:text-lg">{t.about.description}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-5 text-sm leading-relaxed text-ash md:text-base">{t.about.bio}</p>
          </Reveal>
          <Reveal delay={0.3} className="mt-9 flex flex-wrap items-center gap-5">
            <a
              href={SITE.youtube}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-youtube-button"
              className="flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-colors duration-300 hover:bg-emberHover"
            >
              <Youtube className="h-4 w-4" />
              {t.about.cta}
            </a>
            <div className="flex items-center gap-3">
              {socials.map(({ key, url, Icon, testId }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testId}
                  aria-label={key}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ash transition-colors duration-300 hover:border-ember hover:text-ember"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap gap-x-12 gap-y-5 border-t border-line pt-8" data-testid="about-contact">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ember">{t.contact.call}</p>
                <a href={SITE.phoneHref} className="mt-1.5 block font-display text-lg font-bold text-cream transition-colors hover:text-ember" data-testid="about-contact-phone">
                  {SITE.phone}
                </a>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ember">{t.contact.email}</p>
                <a href={`mailto:${SITE.email}`} className="mt-1.5 block font-display text-lg font-bold text-cream transition-colors hover:text-ember" data-testid="about-contact-email">
                  {SITE.email}
                </a>
              </div>
              <p className="w-full text-xs text-ash">{t.contact.collab}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
